<script setup lang="ts">
import { ref, watch, inject } from 'vue';
import { NxtToggle, NxtDropdown } from '@future-harry/nxt-styles';
import { clone, deepEqual } from '@/core/utils';
import CompanySelector from '@/core/components/form-elements/CompanySelector.vue';
import { useJobBoardStore } from '@/core/collections/job-boards/job-board.store';
import { useJobBoard } from '@/core/composables/useJobBoard';
import { useJourneys } from '@/apps/admin/composables/journeys.composable';
import { notifyError, notifySuccess } from '@/core/helpers/notification.helper';

const model = defineModel<any>();

const props = defineProps<{
   errors: Record<string, string>;
}>();

const pageId = inject<string>('pageId') as string;
const jobBoardStore = useJobBoardStore();
const { updateJobBoard } = useJobBoard(pageId);
const { teamOptions, journeyOptions } = useJourneys(model);

const customers = ref(clone(jobBoardStore.currentJobBoard.customers));
watch(
   customers,
   async () => {
      if (!deepEqual(customers.value, jobBoardStore.currentJobBoard.customers)) {
         try {
            jobBoardStore.setCustomers(customers.value);
            await updateJobBoard(true);
            notifySuccess({ title: 'Saved customers' });
         } catch {
            notifyError({ title: 'Could not save customers' });
         }
      }
   },
   { deep: true },
);
</script>

<template>
   <section>
      <h4>{{ $t('live-editor.content-blocks.job_board.linked-companies-title') }}</h4>
      <CompanySelector v-model="customers" />
   </section>
   <section class="job-board-block-edit__filters">
      <h4>{{ $t('live-editor.content-blocks.job_board.display_options') }}</h4>
      <NxtToggle
         name="job-board-filter-category"
         v-model="model.job_types"
         :label="$t('live-editor.content-blocks.job_board.filters.toggle-category')"
      />
      <NxtToggle
         name="job-board-filter-schedule"
         v-model="model.schedules"
         :label="$t('live-editor.content-blocks.job_board.filters.toggle-schedule')"
      />
      <NxtToggle
         name="job-board-filter-location"
         v-model="model.cities"
         :label="$t('live-editor.content-blocks.job_board.filters.toggle-location')"
      />
      <NxtToggle
         name="job-board-filter-search"
         v-model="model.search"
         :label="$t('live-editor.content-blocks.job_board.filters.toggle-search')"
      />
      <NxtToggle
         name="job-board-filter-search_cities"
         v-model="model.search_cities"
         :label="$t('live-editor.content-blocks.job_board.filters.toggle-search_cities')"
      />
   </section>
   <section>
      <h4>{{ $t('live-editor.content-blocks.job_board.spontaneous-apply.title') }}</h4>
      <NxtToggle
         class="mb-gutter-small"
         name="job-board-spontaneous-toggle"
         v-model="model.show_spontaneous_application"
         :label="$t('live-editor.content-blocks.job_board.spontaneous-apply.toggle')"
      />
      <NxtDropdown
         v-if="model.show_spontaneous_application"
         v-model="model.journey_id"
         class="mb-gutter-small"
         name="journey_id"
         :label="$t('live-editor.content-blocks.job_board.journey-label')"
         :options="journeyOptions"
         :errors="errors"
         :search-type="'internal'"
      />
      <NxtDropdown
         v-if="model.show_spontaneous_application && model.journey_id"
         v-model="model.journey_team_id"
         name="journey_team_id"
         :label="$t('live-editor.content-blocks.job_board.team-label')"
         :options="teamOptions"
         :errors="errors"
         :search-type="'internal'"
      />
   </section>
</template>

<style scoped lang="scss">
.job-board-block-edit__filters {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
}

h4 {
   height: var(--nxt-button-height); // other row titles have this height (trash button icon)
   line-height: var(--nxt-button-height);
   border-radius: var(--nxt-radius);
   padding: 0 var(--nxt-gutter);
   background: var(--nxt-medium-dark);
   color: var(--nxt-white);
   margin: var(--nxt-gutter) 0;
}

p {
   color: var(--nxt-medium-dark);
}

.mb-gutter-small {
   margin-bottom: var(--nxt-gutter-small);
}
</style>
